import { mask } from "../../../util/MaskUtil";
import { validateStep } from "../../../util/ValidationUtil";
import { getFieldRegexp } from "../../../util/ValidationRegex";
import {
  CURRENCY_FIELDS,
  SOLE_PROPRIETOR,
  TAX_ID_PLACEHOLDER,
  currencyInput,
  hasSoleProprietorEin,
  joinPrincipalName
} from "../../../util/ApplicationFields";
export {
  currencyInput,
  formatCurrencyInput,
  formatMoney,
  hasSoleProprietorEin
} from "../../../util/ApplicationFields";

// The confirmation is a second plaintext copy of the SSN. It lives only in
// the form state and is removed everywhere the SSN itself is removed.
export const CONFIRM_SSN = "confirmSocialSecurityNumber";

export function changeMerchantField(previous, name, value) {
  const application = { ...previous };
  if (CURRENCY_FIELDS.includes(name)) {
    const raw = currencyInput(value);
    if (raw === null) return previous;
    application[name] = raw;
  } else if (name === CONFIRM_SSN) {
    application[name] = mask("socialSecurityNumber", value);
  } else {
    application[name] = mask(name, value);
  }
  if (name === "socialSecurityNumber") {
    // Editing, including clearing, must not fall back to the saved SSN.
    application.encryptedSocialSecurityNumber = "";
    application.encryptedSocialSecurityNumberSalt = "";
  }
  if (name === "ownershipType" && value !== SOLE_PROPRIETOR) {
    application.soleProprietorHasEin = "";
    if (application.federalTaxId === TAX_ID_PLACEHOLDER)
      application.federalTaxId = "";
  }
  if (name === "soleProprietorHasEin") {
    application.federalTaxId =
      value === "No"
        ? TAX_ID_PLACEHOLDER
        : application.federalTaxId === TAX_ID_PLACEHOLDER
        ? ""
        : application.federalTaxId || "";
  }
  if (
    (name === "firstName" || name === "lastName") &&
    application.ownershipType === SOLE_PROPRIETOR
  ) {
    application.legalBusinessName = joinPrincipalName(application);
  }
  return application;
}

export const PAYMENT_STEP = 5;

// An application with nothing to pay never shows the Payment step.
export const merchantStep = (step, application) =>
  step === PAYMENT_STEP && application.withoutPayment ? step + 1 : step;

export function validateMerchantStep(step, application, signaturePad) {
  // The merchant must never enter CRM-only validation branches on a resumed document.
  const data = {
    ...application,
    repStarted: false,
    isSubmitting: false,
    agree: !!application.agree,
    agreeAdditional: !!application.agreeAdditional
  };
  if (step === 1 && data.ownershipType === SOLE_PROPRIETOR)
    data.legalBusinessName = joinPrincipalName(data);
  const result = validateStep(step, data, signaturePad);
  const errors = { ...result.errors };
  if (step === 1 && data.ownershipType === SOLE_PROPRIETOR) {
    ["firstName", "lastName"].forEach(name => {
      if (!data[name] || !data[name].trim())
        errors[name] =
          name === "firstName"
            ? "First Name is Required"
            : "Last Name is Required";
      else if (!data[name].match(getFieldRegexp(name)))
        errors[name] =
          name === "firstName"
            ? "First Name is an Incorrect Format"
            : "Last Name is an Incorrect Format";
    });
    if (
      hasSoleProprietorEin(data) &&
      (!/^\d{2}-\d{7}$/.test(data.federalTaxId || "") ||
        data.federalTaxId === TAX_ID_PLACEHOLDER)
    ) {
      errors.federalTaxId = "Enter a valid EIN or choose No.";
    }
  }
  if (step === 1 && data.shippingDifferent) {
    const value = name => String(data[name] || "").trim();
    const check = (name, required, format, pattern) => {
      if (!value(name)) errors[name] = required;
      // The shared patterns are global, so match() rather than a stateful test().
      else if (pattern && !value(name).match(pattern)) errors[name] = format;
    };
    check("shippingAddress1", "Shipping Address is Required");
    check("shippingCity", "City is Required", "City is an Incorrect Format", getFieldRegexp("city"));
    check("shippingState", "State is Required", "State is an Incorrect Format", getFieldRegexp("state"));
    check("shippingZip", "Zip Code is Required", "Zip is an Incorrect Format", getFieldRegexp("zip"));
  }
  // A plaintext SSN is either a new one or, in the CRM, the decrypted saved
  // one; both are confirmed. Only a saved SSN without plaintext is skipped.
  if (step === 2 && (data.socialSecurityNumber || !data.encryptedSocialSecurityNumber)) {
    const digits = value => String(value || "").replace(/\D/g, "");
    if (data.socialSecurityNumber && digits(data.socialSecurityNumber).length !== 9)
      errors.socialSecurityNumber = "Social Security Number is an Incorrect Format";
    if (!digits(data[CONFIRM_SSN]))
      errors[CONFIRM_SSN] = "Confirm Social Security Number is Required";
    else if (digits(data[CONFIRM_SSN]) !== digits(data.socialSecurityNumber))
      errors[CONFIRM_SSN] = "Social Security Numbers do not match";
  }
  if (step === 4 && !/^\d{9}$/.test(data.routingNumber || ""))
    errors.routingNumber = "Routing number must contain 9 digits.";
  if (step === 3) {
    const labels = {
      averageSale: "Average Sales Amount",
      highestSale: "Highest Estimated Sales Amount",
      cardSales: "Estimated Monthly Credit and Debit Card Sales"
    };
    CURRENCY_FIELDS.forEach(name => {
      if (!String(data[name] ?? "").trim()) errors[name] = `${labels[name]} is Required`;
    });
  }
  Object.keys(errors).forEach(name => {
    if (/State$/.test(name) && !String(data[name] || "").trim())
      errors[name] = "State is Required";
  });
  return { isValid: Object.keys(errors).length === 0, errors };
}

// Only revalidate errors already shown; typing must not mark untouched fields invalid.
export function refreshMerchantErrors(step, application, previous, signaturePad) {
  if (!Object.keys(previous).length) return previous;
  const { errors } = validateMerchantStep(step, application, signaturePad);
  return Object.keys(previous).reduce((remaining, name) => {
    if (errors[name]) remaining[name] = errors[name];
    return remaining;
  }, {});
}

export function findResumableApplication(documents, linkId) {
  return documents
    .map(document => ({ ...document.data(), id: document.id }))
    .filter(application =>
      !application.completedAt && !application.isCheckout &&
      (!application.status || application.status === "Incomplete") &&
      (!linkId || application.link?.id === linkId)
    )
    .sort((a, b) => {
      const time = value => value?.toMillis ? value.toMillis() :
        value?.seconds ? value.seconds * 1000 : new Date(value || 0).getTime();
      return time(b.createdAt) - time(a.createdAt);
    })[0];
}

// Samples are display-only and never become application fields.
export function getReceiptData(application) {
  const value = (field, sample) =>
    String(application[field] || "").trim() || sample;
  return {
    name: value("doingBusinessAs", "Your Business"),
    street: value("businessAddress1", "123 Main Street"),
    suite: String(application.businessAddress2 || "").trim(),
    city: value("businessCity", "Simi Valley"),
    state: value("businessState", "CA"),
    zip: value("businessZip", "93063"),
    phone: value("businessPhone", "(805) 555-0123"),
    website: value("website", "www.yourbusiness.com").replace(
      /^https?:\/\//i,
      ""
    )
  };
}

export const displayAddress = (application, prefix) =>
  [
    application[`${prefix}Address1`],
    application[`${prefix}Address2`],
    [
      application[`${prefix}City`],
      application[`${prefix}State`],
      application[`${prefix}Zip`]
    ]
      .filter(Boolean)
      .join(" ")
  ]
    .filter(Boolean)
    .join("\n");
